import {roundedString} from "../lib/utils";
import { getClosePositionTimeInForce, getClosePositionOrderType, getClosePositionLimitPrice, resolveTimeInForceForOrderType } from '../utils/marketHours';
import { resolveTradableSymbol } from '../utils/futuresContracts';
import { isOptionSymbol } from '../utils/optionSymbol';

const LIVE_TRADESTATION_API_URL = 'https://api.tradestation.com/v3'
const PAPER_TRADESTATION_API_URL = 'https://sim-api.tradestation.com/v3'

export const DEFAULT_DISTANCE = {
    'MYM': 50,
    'MES': 5,
    'MNQ': 25,
}
export const DEFAULT_TICK_MULTIPLIER = 1;

export const getAccountId = (account) => account?.AccountID;
export const getBaseUrl = (paperTrading) => { return paperTrading ? PAPER_TRADESTATION_API_URL : LIVE_TRADESTATION_API_URL }

// RESOURCE URLS
export const getBarsUrl = (ticker, interval, unit, barsback, sessionTemplate = 'Default') => {
    return `${LIVE_TRADESTATION_API_URL}/marketdata/barcharts/${ticker}?interval=${interval}&unit=${unit}&barsback=${barsback}&sessiontemplate=${encodeURIComponent(sessionTemplate)}`;
}
export const getQuoteSnapshotsUrl = (symbols) => {
    // Backend-proxied to handle auth
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const symbolsParam = encodeURIComponent(symbols);
    return `${BACKEND_URL}/tradestation/marketdata/quotes?symbols=${symbolsParam}`;
}

/** NDJSON quote stream (StreamHub); not the charts WebSocket. */
export const getQuotesStreamUrl = (symbols) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const symbolsParam = encodeURIComponent(symbols);
    return `${BACKEND_URL}/tradestation/stream/quotes?symbols=${symbolsParam}`;
}
export const getTickerUrl = (ticker) => {
    return `${LIVE_TRADESTATION_API_URL}/marketdata/symbols/${ticker}`;
}
export const getAccountsUrl = (paperTrading) => {
    return `${getBaseUrl(paperTrading)}/brokerage/accounts`;
}
export const getBalancesUrl = (paperTrading, selectedAccount) => {
    return `${getBaseUrl(paperTrading)}/brokerage/accounts/${getAccountId(selectedAccount)}/balances`;
}
export const getPositionsUrl = (paperTrading, selectedAccount) => {
    return `${getBaseUrl(paperTrading)}/brokerage/accounts/${getAccountId(selectedAccount)}/positions`;
}
export const getOrdersUrl = (paperTrading, selectedAccount) => {
    return `${getBaseUrl(paperTrading)}/brokerage/accounts/${getAccountId(selectedAccount)}/orders`;
}
export const getHistoricalOrdersUrl = (paperTrading, selectedAccount, since = null, pageSize = 1000, nextToken = null) => {
    let url = `${getBaseUrl(paperTrading)}/brokerage/accounts/${getAccountId(selectedAccount)}/historicalorders?pageSize=${pageSize}`;
    if (since) {
        url += `&since=${since}`;
    }
    if (nextToken) {
        url += `&nextToken=${encodeURIComponent(nextToken)}`;
    }
    return url;
}
export const createOrderUrl = (paperTrading) => {
    return `${getBaseUrl(paperTrading)}/orderexecution/orders`;
}
export const openOrderUrl = (paperTrading, orderID) => {
    return `${getBaseUrl(paperTrading)}/orderexecution/orders/${orderID}`;
}
export const createBracketOrderUrl = (paperTrading) => {
    return `${getBaseUrl(paperTrading)}/orderexecution/ordergroups`;
}

// STREAM URLS (HTTP: quote NDJSON; bars + market depth use WebSocket /ws/charts in the app)
export const marketAggregatesUrl = (paperTrading, ticker) => {
    // Backend-proxied stream to avoid CORS and hide TS token
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    return `${BACKEND_URL}/tradestation/marketdata/stream/marketdepth/aggregates/${ticker}`;
}

export const baseOrderPayload = (selectedAccount) => {
    return {
        "AccountID": getAccountId(selectedAccount),
        "Route": "Intelligent",
    }
}

function roundToQuarter(number) {
    return Math.round(number / 0.25) * 0.25;
}

function roundToWhole(number) {
    return Math.round(number);
}

export const roundToIncrement = (number, increment, roundUp) => {
    let roundedNumber;
    if (roundUp) {
        roundedNumber = Math.ceil(number / increment) * increment;
    } else {
        roundedNumber = Math.floor(number / increment) * increment;
    }
    return roundedNumber;
}

// ORDER MANAGEMENT FUNCTIONS


export const generateFibonacciOrders = (selectedAccount, selectedTicker, selectedTickerDetails, totalQuantity, stopLoss, longShort, limitPrice, isFutures) => {
    let fib_38, fib_25;
    const range = 2 * Math.abs(limitPrice - stopLoss)
    if (longShort === 'Long') {
        fib_38 = stopLoss + 0.38 * range;
        fib_25 = stopLoss + 0.25 * range;
    } else {
        fib_38 = stopLoss - 0.38 * range;
        fib_25 = stopLoss - 0.25 * range;
    }

    fib_38 = roundToIncrement(fib_38, selectedTickerDetails.PriceFormat.Increment, longShort === 'Long');
    fib_25 = roundToIncrement(fib_25, selectedTickerDetails.PriceFormat.Increment, longShort === 'Long');

    // Calculate the base quantity for each order
    let baseQuantity = Math.floor(totalQuantity / 3);

    // Calculate the remaining quantity
    let remainingQuantity = totalQuantity % 3;

    // Initialize the quantities for each order with the base quantity
    let orderQuantities = [baseQuantity, baseQuantity, baseQuantity];

    // Distribute the remaining quantity to the first few orders
    for (let i = 0; i < remainingQuantity; i++) {
        orderQuantities[i]++;
    }

    let order1 = generateOrderWithBracketPayload(
      selectedAccount,
      selectedTicker,
      orderQuantities[0],
      stopLoss,
      longShort,
      limitPrice,
      1.00,
      isFutures
    )

    let order2 = generateOrderWithBracketPayload(
      selectedAccount,
      selectedTicker,
      orderQuantities[1],
      stopLoss,
      longShort,
      roundedString(fib_38, selectedTickerDetails.PriceFormat.Decimals),
      1.00,
      isFutures
    )

    let order3 = generateOrderWithBracketPayload(
      selectedAccount,
      selectedTicker,
      orderQuantities[2],
      stopLoss,
      longShort,
      roundedString(fib_25, selectedTickerDetails.PriceFormat.Decimals),
      1.00,
      isFutures
    )

    // Remove any orders with a quantity of 0
    let orders = [order1, order2, order3].filter(order => order.Quantity > 0);

    return orders;
}
// CREATE NEW ORDER WITH SL & TP
export const generateOrderWithBracketPayload = (selectedAccount, ticker, quantity, stopLoss, longShort, limitPrice, takeProfitPercentage, isFutures, tickerDetails = null) => {
    const tradeAction = getTradeAction(longShort, true, isFutures, isOptionSymbol(ticker));
    const bracketOrderPayload = generateBracketOrderPayload(selectedAccount, ticker, quantity, stopLoss, longShort, limitPrice, takeProfitPercentage, isFutures, tickerDetails)

    const order = {
        "Symbol": ticker,
        "Quantity": quantity.toString(),
        "OrderType": 'Market',
        "TradeAction": tradeAction,
        "TimeInForce": {"Duration": "DAY"}
    }

    if (limitPrice) {
        const roundedPrice = roundToTickSizeFromDetails(limitPrice, ticker, tickerDetails);
        order["LimitPrice"] = roundedString(roundedPrice, getPrecision(ticker, tickerDetails));
        order["OrderType"] = 'Limit';
    }

    const generatedOrder = { ...baseOrderPayload(selectedAccount), ...order };

    generatedOrder["OSOs"] = bracketOrderPayload;

    return generatedOrder;
}

// CREATE BRACKET ORDER
// Does not include order to open new position, just SL & TP
export const generateBracketOrderPayload = (selectedAccount, ticker, quantity, stopLoss, longShort, limitPrice, takeProfitPercentage, isFutures, tickerDetails = null) => {
    const midpoint = parseFloat(limitPrice);
    const stopDistance = Math.abs(midpoint - stopLoss);

    const takeProfitOrderType = 'Limit'
    const stopLossOrderType = 'StopMarket'
    const takeProfitTradeAction = getTradeAction(longShort, false, isFutures);
    const stopLossTradeAction = getTradeAction(longShort, false, isFutures);

    let rLevel = 1;
    let brackets = [];
    let remainingQty = quantity;

    while (remainingQty > 0) {
        const takeProfitDistance = rLevel * stopDistance;

        let takeProfitQty = Math.round(remainingQty * takeProfitPercentage)
        let takeProfitPrice = longShort === 'Long' ? midpoint + takeProfitDistance : midpoint - takeProfitDistance;

        takeProfitQty = takeProfitQty < 1 ? 1 : takeProfitQty; // round the takeProfitQty up to 1 if less than 1

        const takeProfitOrder = { // TAKE PROFIT
            "Symbol": ticker,
            "Quantity": takeProfitQty.toString(),
            "OrderType": takeProfitOrderType,
            "TradeAction": takeProfitTradeAction,
            "LimitPrice": roundedString(takeProfitPrice, getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": "GTC"},
            "OrderID": `TP_${Date.now()}`,
            ...baseOrderPayload(selectedAccount)
        }

        const stopLossOrder = {
            "Symbol": ticker,
            "Quantity": takeProfitQty.toString(),
            "OrderType": stopLossOrderType,
            "TradeAction": stopLossTradeAction,
            "StopPrice": roundedString(roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails), getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": "GTC"},
            "OrderID": `SL_${Date.now()}`,
            ...baseOrderPayload(selectedAccount)
        }

        brackets.push({
            "Type": 'BRK',
            "Orders": [takeProfitOrder, stopLossOrder]
        });

        remainingQty -= takeProfitQty;
        rLevel += 1;
    }

    return brackets;
}

// CREATE ORDER WITH FLEXIBLE BRACKET TYPE
export const generateOrderWithFlexibleBracket = (selectedAccount, ticker, quantity, stopLoss, longShort, limitPrice, bracketType, takeProfitPercentage, isFutures = false, existingPosition = null, tickerDetails = null, currentQuote = null, orderType = 'market', timeInForce = 'DAY', userTpPrices = null) => {
    console.debug('generateOrderWithFlexibleBracket called with:', { 
        ticker, 
        longShort, 
        orderType, 
        quantity,
        limitPrice, 
        existingPosition: existingPosition?.LongShort,
        existingQuantity: existingPosition?.Quantity,
        isFutures 
    });
    
    // Determine if this is a closing order or adding to position
    let directionForTradeAction = longShort;
    let isNewPosition = !existingPosition;
    
    if (existingPosition) {
        // Check if we're going opposite direction (closing) or same direction (adding)
        const positionDirection = existingPosition.LongShort;
        const orderDirection = longShort;
        
        if (positionDirection !== orderDirection) {
            // Opposite direction = closing the position
            // For closing, we pass the POSITION direction to getTradeAction
            // which will return the appropriate closing action
            directionForTradeAction = positionDirection;
            isNewPosition = false;
        } else {
            // Same direction = adding to position, use the intended direction
            directionForTradeAction = longShort;
            isNewPosition = true;
        }
    }
    
    const tradeAction = getTradeAction(directionForTradeAction, isNewPosition, isFutures, isOptionSymbol(ticker));
    console.debug('Trade action determined:', { directionForTradeAction, isNewPosition, tradeAction });

    const order = {
        "Symbol": ticker,
        "Quantity": quantity.toString(),
        "OrderType": 'Market',
        "TradeAction": tradeAction,
        "TimeInForce": {"Duration": timeInForce}
    }

    if (orderType === 'limit' && limitPrice) {
        const roundedPrice = roundToTickSizeFromDetails(limitPrice, ticker, tickerDetails);
        const precision = getPrecision(ticker, tickerDetails);
        order["LimitPrice"] = roundedString(roundedPrice, precision);
        order["OrderType"] = 'Limit';
    } else if (orderType === 'stop' && limitPrice) {
        const roundedPrice = roundToTickSizeFromDetails(limitPrice, ticker, tickerDetails);
        const precision = getPrecision(ticker, tickerDetails);
        order["StopPrice"] = roundedString(roundedPrice, precision);
        order["OrderType"] = 'StopMarket';
    } else if (orderType === 'stop_limit' && limitPrice && typeof stopLoss === 'number') {
        // Stop Limit entry: StopPrice from stop input, LimitPrice from limit input
        const roundedStop = roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails);
        const roundedLimit = roundToTickSizeFromDetails(limitPrice, ticker, tickerDetails);
        const precision = getPrecision(ticker, tickerDetails);
        order["StopPrice"] = roundedString(roundedStop, precision);
        order["LimitPrice"] = roundedString(roundedLimit, precision);
        order["OrderType"] = 'StopLimit';
    }

    order["TimeInForce"] = {"Duration": resolveTimeInForceForOrderType(order["OrderType"], timeInForce)};

    const generatedOrder = { ...baseOrderPayload(selectedAccount), ...order };

    // Add brackets based on bracket type
    if (bracketType !== 'none') {
        // For market orders with brackets, use current quote price for bracket calculations
        const bracketReferencePrice = limitPrice || currentQuote;
        
        const bracketOrderPayload = generateFlexibleBracketOrderPayload(
            selectedAccount,
            ticker,
            quantity,
            stopLoss,
            longShort,
            bracketReferencePrice,
            bracketType,
            takeProfitPercentage,
            isFutures,
            tickerDetails,
            userTpPrices
        );
        generatedOrder["OSOs"] = bracketOrderPayload;
    }

    return generatedOrder;
}

// CREATE FLEXIBLE BRACKET ORDER BASED ON TYPE
export const generateFlexibleBracketOrderPayload = (selectedAccount, ticker, quantity, stopLoss, longShort, limitPrice, bracketType, takeProfitPercentage, isFutures = false, tickerDetails = null, userTpPrices = null) => {
    const midpoint = parseFloat(limitPrice);
    const stopDistance = Math.abs(midpoint - stopLoss);
    
    // For bracket orders, we need the opposite trade action of the main entry order
    // If main entry is BUY (Long), then stop loss and take profit should be SELL
    const takeProfitTradeAction = getClosePositionTradeAction(longShort, isFutures);
    const stopLossTradeAction = getClosePositionTradeAction(longShort, isFutures);

    let brackets = [];

    if (bracketType === 'stop_only') {
        // Only stop loss
        const roundedStopLoss = roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails);
        const stopLossOrder = {
            "Symbol": ticker,
            "Quantity": quantity.toString(),
            "OrderType": 'StopMarket',
            "TradeAction": stopLossTradeAction,
            "StopPrice": roundedString(roundedStopLoss, getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": "GTC"},
            ...baseOrderPayload(selectedAccount)
        };

        brackets.push({
            "Type": 'BRK',
            "Orders": [stopLossOrder]
        });
    } else if (bracketType === 'tp_1') {
        // Stop loss + 1 take profit (use user-defined TP if provided)
        let takeProfitPrice = null;
        if (Array.isArray(userTpPrices) && Number.isFinite(parseFloat(userTpPrices[0]))) {
            takeProfitPrice = roundToTickSizeFromDetails(parseFloat(userTpPrices[0]), ticker, tickerDetails);
        } else {
            const takeProfitDistance = stopDistance;
            takeProfitPrice = longShort === 'Long' ? midpoint + takeProfitDistance : midpoint - takeProfitDistance;
            takeProfitPrice = roundToTickSizeFromDetails(takeProfitPrice, ticker, tickerDetails);
        }

        const takeProfitOrder = {
            "Symbol": ticker,
            "Quantity": quantity.toString(),
            "OrderType": 'Limit',
            "TradeAction": takeProfitTradeAction,
            "LimitPrice": roundedString(takeProfitPrice, getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": "GTC"},
            ...baseOrderPayload(selectedAccount)
        };

        const stopLossOrder = {
            "Symbol": ticker,
            "Quantity": quantity.toString(),
            "OrderType": 'StopMarket',
            "TradeAction": stopLossTradeAction,
            "StopPrice": roundedString(roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails), getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": "GTC"},
            ...baseOrderPayload(selectedAccount)
        };

        brackets.push({
            "Type": 'BRK',
            "Orders": [takeProfitOrder, stopLossOrder]
        });
    } else if (bracketType === 'tp_2') {
        // Check if quantity is too small for 2 take profits
        if (quantity < 2) {
            // Fall back to single take profit when quantity is 1
            let takeProfitPrice = null;
            if (Array.isArray(userTpPrices) && Number.isFinite(parseFloat(userTpPrices[0]))) {
                takeProfitPrice = roundToTickSizeFromDetails(parseFloat(userTpPrices[0]), ticker, tickerDetails);
            } else {
                const takeProfitDistance = stopDistance;
                takeProfitPrice = longShort === 'Long' ? midpoint + takeProfitDistance : midpoint - takeProfitDistance;
                takeProfitPrice = roundToTickSizeFromDetails(takeProfitPrice, ticker, tickerDetails);
            }
// console.debug(limitPrice, midpoint, takeProfitDistance, takeProfitPrice)
            const takeProfitOrder = {
                "Symbol": ticker,
                "Quantity": quantity.toString(),
                "OrderType": 'Limit',
                "TradeAction": takeProfitTradeAction,
                "LimitPrice": roundedString(takeProfitPrice, getPrecision(ticker, tickerDetails)),
                "TimeInForce": {"Duration": "GTC"},
                ...baseOrderPayload(selectedAccount)
            };

            const stopLossOrder = {
                "Symbol": ticker,
                "Quantity": quantity.toString(),
                "OrderType": 'StopMarket',
                "TradeAction": stopLossTradeAction,
                "StopPrice": roundedString(roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails), getPrecision(ticker, tickerDetails)),
                "TimeInForce": {"Duration": "GTC"},
                ...baseOrderPayload(selectedAccount)
            };

            brackets.push({
                "Type": 'BRK',
                "Orders": [takeProfitOrder, stopLossOrder]
            });
        } else {
            // Stop loss + 2 take profits - create separate brackets for each (prefer user TP prices)
            let takeProfit1Price = null;
            let takeProfit2Price = null;
            if (Array.isArray(userTpPrices)) {
                if (Number.isFinite(parseFloat(userTpPrices[0]))) {
                    takeProfit1Price = roundToTickSizeFromDetails(parseFloat(userTpPrices[0]), ticker, tickerDetails);
                }
                if (Number.isFinite(parseFloat(userTpPrices[1]))) {
                    takeProfit2Price = roundToTickSizeFromDetails(parseFloat(userTpPrices[1]), ticker, tickerDetails);
                }
            }
            if (!Number.isFinite(takeProfit1Price)) {
                const takeProfit1Distance = stopDistance;
                takeProfit1Price = longShort === 'Long' ? midpoint + takeProfit1Distance : midpoint - takeProfit1Distance;
                takeProfit1Price = roundToTickSizeFromDetails(takeProfit1Price, ticker, tickerDetails);
            }
            if (!Number.isFinite(takeProfit2Price)) {
                const takeProfit2Distance = 2 * stopDistance;
                takeProfit2Price = longShort === 'Long' ? midpoint + takeProfit2Distance : midpoint - takeProfit2Distance;
                takeProfit2Price = roundToTickSizeFromDetails(takeProfit2Price, ticker, tickerDetails);
            }

            // Use Math.floor to ensure we never exceed total quantity
            const takeProfit1Qty = Math.floor(quantity * 0.5);
            const takeProfit2Qty = quantity - takeProfit1Qty;
            
            // Validate we're not exceeding total
            if (takeProfit1Qty + takeProfit2Qty > quantity) {
                console.error(`[generateFlexibleBracketOrderPayload] CRITICAL: Allocated ${takeProfit1Qty + takeProfit2Qty} > ${quantity}!`);
                throw new Error(`Cannot allocate more shares than available`);
            }
            
            // Skip if either quantity is 0
            if (takeProfit1Qty === 0 || takeProfit2Qty === 0) {
                console.warn(`[generateFlexibleBracketOrderPayload] Insufficient quantity (${quantity}) for 2 take profits. Need at least 2 shares.`);
                // Fall back to single TP with all quantity
                const fallbackQty = quantity;
                const fallbackTpPrice = takeProfit1Price; // Use first TP price
                
                const takeProfitOrder = {
                    "Symbol": ticker,
                    "Quantity": fallbackQty.toString(),
                    "OrderType": 'Limit',
                    "TradeAction": takeProfitTradeAction,
                    "LimitPrice": roundedString(fallbackTpPrice, getPrecision(ticker, tickerDetails)),
                    "TimeInForce": {"Duration": "GTC"},
                    ...baseOrderPayload(selectedAccount)
                };
                
                const stopLossOrder = {
                    "Symbol": ticker,
                    "Quantity": fallbackQty.toString(),
                    "OrderType": 'StopMarket',
                    "TradeAction": stopLossTradeAction,
                    "StopPrice": roundedString(roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails), getPrecision(ticker, tickerDetails)),
                    "TimeInForce": {"Duration": "GTC"},
                    ...baseOrderPayload(selectedAccount)
                };
                
                brackets.push({
                    "Type": 'BRK',
                    "Orders": [takeProfitOrder, stopLossOrder]
                });
                
                return brackets;
            }
            
            console.log(`[generateFlexibleBracketOrderPayload] 2 TP allocation: TP1=${takeProfit1Qty}, TP2=${takeProfit2Qty}, total=${quantity}`);

        // First bracket: First take profit + corresponding stop loss
        const takeProfit1Order = {
            "Symbol": ticker,
            "Quantity": takeProfit1Qty.toString(),
            "OrderType": 'Limit',
            "TradeAction": takeProfitTradeAction,
                            "LimitPrice": roundedString(takeProfit1Price, getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": "GTC"},
            ...baseOrderPayload(selectedAccount)
        };

        const stopLoss1Order = {
            "Symbol": ticker,
            "Quantity": takeProfit1Qty.toString(),
            "OrderType": 'StopMarket',
            "TradeAction": stopLossTradeAction,
            "StopPrice": roundedString(roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails), getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": "GTC"},
            ...baseOrderPayload(selectedAccount)
        };

        // Second bracket: Second take profit + corresponding stop loss
        const takeProfit2Order = {
            "Symbol": ticker,
            "Quantity": takeProfit2Qty.toString(),
            "OrderType": 'Limit',
            "TradeAction": takeProfitTradeAction,
                            "LimitPrice": roundedString(takeProfit2Price, getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": "GTC"},
            ...baseOrderPayload(selectedAccount)
        };

        const stopLoss2Order = {
            "Symbol": ticker,
            "Quantity": takeProfit2Qty.toString(),
            "OrderType": 'StopMarket',
            "TradeAction": stopLossTradeAction,
            "StopPrice": roundedString(roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails), getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": "GTC"},
            ...baseOrderPayload(selectedAccount)
        };

        // Create two separate brackets
        brackets.push({
            "Type": 'BRK',
            "Orders": [takeProfit1Order, stopLoss1Order]
        });

        brackets.push({
            "Type": 'BRK',
            "Orders": [takeProfit2Order, stopLoss2Order]
        });
        }
    }

    return brackets;
}

/**
 * Generate bracket orders from a template configuration.
 * Supports N take profits with custom percentages and R-multiples.
 * Each take profit gets its own bracket with a corresponding stop loss.
 * 
 * @param {Object} selectedAccount - The trading account
 * @param {string} ticker - The ticker symbol
 * @param {number} quantity - Total position quantity
 * @param {number} stopLoss - Stop loss price
 * @param {string} longShort - 'Long' or 'Short'
 * @param {number} limitPrice - Entry/limit price (reference for R calculations)
 * @param {Object} template - Bracket template with { takeProfits: [{ percentage, rMultiple }], atrMultiplier }
 * @param {boolean} isFutures - Whether this is a futures contract
 * @param {Object} tickerDetails - Ticker details for precision/rounding
 * @param {Array} userTpPrices - Optional override prices for take profits (absolute prices)
 * @param {string} timeInForce - Order duration (DAY, GTC, DYP, GCP, …). Defaults to GTC for exit brackets.
 * @returns {Array} Array of bracket order groups
 */
export const generateBracketOrdersFromTemplate = (
    selectedAccount, 
    ticker, 
    quantity, 
    stopLoss, 
    longShort, 
    limitPrice, 
    template,
    isFutures = false, 
    tickerDetails = null,
    userTpPrices = null,
    timeInForce = 'GTC'
) => {
    const midpoint = parseFloat(limitPrice);
    const stopDistance = Math.abs(midpoint - stopLoss);
    const duration = timeInForce || 'GTC';
    
    // Bracket exit orders use opposite action of entry
    const takeProfitTradeAction = getClosePositionTradeAction(longShort, isFutures);
    const stopLossTradeAction = getClosePositionTradeAction(longShort, isFutures);
    
    let brackets = [];
    const takeProfits = template?.takeProfits || [];
    const stopLosses = template?.stopLosses || [];
    
    // CRITICAL: If takeProfits exist, ignore stopLosses completely
    // Each TP bracket already includes a stop loss with matching quantity
    // Processing stopLosses separately would create duplicate stop-loss coverage
    if (takeProfits.length > 0 && stopLosses.length > 0) {
        console.warn(`[generateBracketOrdersFromTemplate] Template has both takeProfits (${takeProfits.length}) and stopLosses (${stopLosses.length}). Ignoring stopLosses - each TP bracket already includes a stop loss.`);
    }
    
    // If no take profits defined, just create stop loss only
    if (takeProfits.length === 0) {
        const roundedStopLoss = roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails);
        const stopLossOrder = {
            "Symbol": ticker,
            "Quantity": quantity.toString(),
            "OrderType": 'StopMarket',
            "TradeAction": stopLossTradeAction,
            "StopPrice": roundedString(roundedStopLoss, getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": resolveTimeInForceForOrderType('StopMarket', duration)},
            ...baseOrderPayload(selectedAccount)
        };
        
        brackets.push({
            "Type": 'BRK',
            "Orders": [stopLossOrder]
        });
        
        return brackets;
    }
    
    // CRITICAL VALIDATION: Check template percentages FIRST
    const totalPercentage = takeProfits.reduce((sum, tp) => sum + (tp.percentage || 0), 0);
    console.log(`[generateBracketOrdersFromTemplate] Template percentages check: ${totalPercentage}% (target: 100%)`);
    
    if (totalPercentage !== 100) {
        const errorMsg = `❌ INVALID TEMPLATE: Percentages sum to ${totalPercentage}%, must be exactly 100%. This order will NOT be placed.`;
        console.error(errorMsg);
        console.error('[ERROR] Template config:', takeProfits.map((tp, i) => `TP${i+1}: ${tp.percentage}%@${tp.rMultiple}R`));
        alert(errorMsg + '\n\nPlease fix your bracket template configuration.');
        throw new Error(errorMsg);
    }
    
    // CRITICAL: Calculate quantities for each take profit level using balanced distribution
    // This ensures we NEVER exceed the total quantity AND distributes remainder evenly
    console.log(`[generateBracketOrdersFromTemplate] Starting allocation for ${quantity} shares across ${takeProfits.length} TPs`);
    console.log(`[generateBracketOrdersFromTemplate] Template percentages:`, takeProfits.map((tp, i) => `TP${i+1}: ${tp.percentage}%`));
    
    // Step 1: Calculate base quantity for each TP (using Math.floor)
    const tpBaseQuantities = takeProfits.map((tp) => {
        return Math.floor((tp.percentage / 100) * quantity);
    });
    
    // Step 2: Calculate total allocated so far and remainder
    const totalAllocated = tpBaseQuantities.reduce((sum, qty) => sum + qty, 0);
    let remainder = quantity - totalAllocated;
    
    console.log(`[generateBracketOrdersFromTemplate] Base allocation: ${tpBaseQuantities.map((q, i) => `TP${i+1}=${q}`).join(', ')} | Total: ${totalAllocated}, Remainder: ${remainder}`);
    
    // Step 3: Distribute remainder by giving +1 to TPs with largest fractional parts
    // This ensures the remainder is distributed evenly rather than all to the last TP
    const tpQuantities = [...tpBaseQuantities];
    
    if (remainder > 0) {
        // Calculate fractional parts for each TP (amount lost due to Math.floor)
        const fractionalParts = takeProfits.map((tp, index) => ({
            index,
            fractionalPart: ((tp.percentage / 100) * quantity) - tpBaseQuantities[index],
            baseQty: tpBaseQuantities[index]
        }));
        
        // Sort by fractional part (descending) - TPs with largest fractional parts get +1 first
        fractionalParts.sort((a, b) => {
            // Primary sort: fractional part (descending)
            if (Math.abs(a.fractionalPart - b.fractionalPart) > 0.0001) {
                return b.fractionalPart - a.fractionalPart;
            }
            // Secondary sort: index (ascending) for determinism when fractional parts are equal
            return a.index - b.index;
        });
        
        // Give +1 to the first 'remainder' TPs (those with largest fractional parts)
        for (let i = 0; i < remainder && i < fractionalParts.length; i++) {
            const tpIndex = fractionalParts[i].index;
            tpQuantities[tpIndex]++;
            console.log(`[generateBracketOrdersFromTemplate] TP${tpIndex + 1}: +1 share (fractional part: ${fractionalParts[i].fractionalPart.toFixed(4)})`);
        }
    }
    
    // CRITICAL VALIDATION: Ensure total allocated quantity equals input quantity
    const finalTotalAllocated = tpQuantities.reduce((sum, qty) => sum + qty, 0);
    
    console.log(`[generateBracketOrdersFromTemplate] FINAL ALLOCATION: ${tpQuantities.map((q, i) => `TP${i+1}=${q}`).join(', ')} | Total: ${finalTotalAllocated}/${quantity}`);
    
    if (finalTotalAllocated !== quantity) {
        const errorMsg = `❌ QUANTITY MISMATCH: Trying to allocate ${finalTotalAllocated} shares but only have ${quantity} shares. This order will NOT be placed.`;
        console.error(errorMsg);
        console.error('[ERROR] Template config:', { takeProfits: takeProfits.map(tp => ({ percentage: tp.percentage, rMultiple: tp.rMultiple })) });
        console.error('[ERROR] Calculated quantities:', tpQuantities);
        alert(errorMsg + '\n\nPlease check your bracket template percentages - they may not add up to 100%.');
        throw new Error(errorMsg);
    }
    
    console.log(`✅ Bracket quantity validation PASSED`);
    
    // Create a bracket for each take profit
    takeProfits.forEach((tp, index) => {
        const tpQty = tpQuantities[index];
        if (tpQty <= 0) {
            console.warn(`[generateBracketOrdersFromTemplate] Skipping TP${index + 1} - allocated 0 quantity (${tp.percentage}%)`);
            return; // Skip if no shares allocated
        }
        
        console.log(`[generateBracketOrdersFromTemplate] Creating TP${index + 1} order: ${tpQty} shares @ ${tp.rMultiple}R`);
        
        // Calculate take profit price from R-multiple or use override
        let takeProfitPrice;
        if (Array.isArray(userTpPrices) && Number.isFinite(parseFloat(userTpPrices[index]))) {
            takeProfitPrice = roundToTickSizeFromDetails(parseFloat(userTpPrices[index]), ticker, tickerDetails);
        } else {
            const takeProfitDistance = (tp.rMultiple || 1) * stopDistance;
            takeProfitPrice = longShort === 'Long' 
                ? midpoint + takeProfitDistance 
                : midpoint - takeProfitDistance;
            takeProfitPrice = roundToTickSizeFromDetails(takeProfitPrice, ticker, tickerDetails);
        }
        
        const takeProfitOrder = {
            "Symbol": ticker,
            "Quantity": tpQty.toString(),
            "OrderType": 'Limit',
            "TradeAction": takeProfitTradeAction,
            "LimitPrice": roundedString(takeProfitPrice, getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": duration},
            ...baseOrderPayload(selectedAccount)
        };
        
        // Each TP gets its own corresponding stop loss with matching quantity
        const stopLossOrder = {
            "Symbol": ticker,
            "Quantity": tpQty.toString(),
            "OrderType": 'StopMarket',
            "TradeAction": stopLossTradeAction,
            "StopPrice": roundedString(roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails), getPrecision(ticker, tickerDetails)),
            "TimeInForce": {"Duration": resolveTimeInForceForOrderType('StopMarket', duration)},
            ...baseOrderPayload(selectedAccount)
        };
        
        // Create bracket with both TP and SL orders
        brackets.push({
            "Type": 'BRK',
            "Orders": [takeProfitOrder, stopLossOrder]
        });
    });
    
    // CRITICAL: When we have take profits, each bracket already includes a stop loss
    // DO NOT add any additional stop-loss-only brackets for the entire position
    // This would cause double-stop-loss coverage and is incorrect
    // If template.stopLosses exists, it should only be used when there are NO takeProfits
    
    return brackets;
}

/**
 * Generate a complete order with brackets from a template.
 * This is the main entry point for template-based order creation.
 */
export const generateOrderWithTemplateBracket = (
    selectedAccount,
    ticker,
    quantity,
    stopLoss,
    longShort,
    limitPrice,
    template,
    isFutures = false,
    existingPosition = null,
    tickerDetails = null,
    currentQuote = null,
    orderType = 'market',
    timeInForce = 'DAY',
    userTpPrices = null
) => {
    ticker = resolveTradableSymbol(ticker) || ticker;
    console.debug('generateOrderWithTemplateBracket called with:', { 
        ticker, 
        longShort, 
        orderType, 
        quantity,
        limitPrice,
        template: template?.name,
        takeProfits: template?.takeProfits?.length,
        isFutures 
    });
    
    // Determine trade action based on position context
    let directionForTradeAction = longShort;
    let isNewPosition = !existingPosition;
    
    if (existingPosition) {
        const positionDirection = existingPosition.LongShort;
        const orderDirection = longShort;
        
        if (positionDirection !== orderDirection) {
            directionForTradeAction = positionDirection;
            isNewPosition = false;
        } else {
            directionForTradeAction = longShort;
            isNewPosition = true;
        }
    }
    
    const optionOrder = isOptionSymbol(ticker);
    const tradeAction = getTradeAction(directionForTradeAction, isNewPosition, isFutures, optionOrder);
    
    const order = {
        "Symbol": ticker,
        "Quantity": quantity.toString(),
        "OrderType": 'Market',
        "TradeAction": tradeAction,
        "TimeInForce": {"Duration": timeInForce}
    };
    
    // Set order type based on user selection
    if (orderType === 'limit' && limitPrice) {
        const roundedPrice = roundToTickSizeFromDetails(limitPrice, ticker, tickerDetails);
        const precision = getPrecision(ticker, tickerDetails);
        order["LimitPrice"] = roundedString(roundedPrice, precision);
        order["OrderType"] = 'Limit';
    } else if (orderType === 'stop' && limitPrice) {
        const roundedPrice = roundToTickSizeFromDetails(limitPrice, ticker, tickerDetails);
        const precision = getPrecision(ticker, tickerDetails);
        order["StopPrice"] = roundedString(roundedPrice, precision);
        order["OrderType"] = 'StopMarket';
    } else if (orderType === 'stop_limit' && limitPrice && typeof stopLoss === 'number') {
        const roundedStop = roundToTickSizeFromDetails(stopLoss, ticker, tickerDetails);
        const roundedLimit = roundToTickSizeFromDetails(limitPrice, ticker, tickerDetails);
        const precision = getPrecision(ticker, tickerDetails);
        order["StopPrice"] = roundedString(roundedStop, precision);
        order["LimitPrice"] = roundedString(roundedLimit, precision);
        order["OrderType"] = 'StopLimit';
    }

    order["TimeInForce"] = {"Duration": resolveTimeInForceForOrderType(order["OrderType"], timeInForce)};
    
    const generatedOrder = { ...baseOrderPayload(selectedAccount), ...order };
    
    // Add brackets based on template (skip if template is null, 'none', or has no stop/TPs)
    // When targeting mode is disabled, template will be null, so no brackets will be added
    const isNoneTemplate = optionOrder || !template || template?.id === 'none' || (!template?.takeProfits?.length && !template?.stopLosses?.length && !template?.atrMultiplier);
    
    if (!isNoneTemplate && stopLoss) {
        const bracketReferencePrice = limitPrice || currentQuote;
        
        const bracketOrderPayload = generateBracketOrdersFromTemplate(
            selectedAccount,
            ticker,
            quantity,
            stopLoss,
            longShort,
            bracketReferencePrice,
            template,
            isFutures,
            tickerDetails,
            userTpPrices,
            timeInForce
        );
        
        if (bracketOrderPayload.length > 0) {
            generatedOrder["OSOs"] = bracketOrderPayload;
        }
    }
    
    return generatedOrder;
}

// UPDATE EXISTING STOP LOSS ORDER
export const generateStopLossUpdatePayload = (currentPosition) => {
    let breakEvenPrice = parseFloat(currentPosition.AveragePrice)
// console.debug('currentPosition', currentPosition)

    if (currentPosition.Symbol.includes('YM')) {
        breakEvenPrice = roundToWhole(breakEvenPrice)
    } else if (currentPosition.AssetType === 'FUTURE') {
        breakEvenPrice = roundToQuarter(breakEvenPrice)
    }

    return { StopPrice: roundedString(breakEvenPrice) }
}

// CREATE NEW BRACKET FOR EXISTING POSITION
export const generateNewBracketOrderPayload = (paperTrading, position, stopLoss, takeProfitRLevel=1, takeProfitPercentage= 0.33, isFutures) => {
    const stopLossOrder = generateStopLossOrderPayload(paperTrading, position, stopLoss, false, isFutures)
    const takeProfitOrder = generateTakeProfitOrder(paperTrading, position, stopLoss, takeProfitRLevel, takeProfitPercentage, isFutures);

    return {
        "Type": "BRK",
        "Orders": [stopLossOrder, takeProfitOrder]
    }
}

// CLOSE EXISTING POSITION
export const generateClosePositionPayload = (selectedAccount, position, isFutures, tickerDetails = null, quantityOverride = null) => {
    // Validate position has required fields
    if (!position || !position.LongShort) {
        throw new Error(`Invalid position data: LongShort is missing. Position: ${JSON.stringify(position)}`);
    }
    if (position.LongShort !== 'Long' && position.LongShort !== 'Short') {
        throw new Error(`Invalid position data: LongShort must be 'Long' or 'Short', got "${position.LongShort}"`);
    }
    if (!position.Symbol) {
        throw new Error(`Invalid position data: Symbol is missing`);
    }
    if (!position.Quantity || Math.abs(parseFloat(position.Quantity)) === 0) {
        throw new Error(`Invalid position data: Quantity is zero or missing`);
    }

    const tradeAction = getClosePositionTradeAction(
        position.LongShort,
        isFutures,
        isOptionSymbol(position.Symbol)
    );
    if (!tradeAction) {
        throw new Error(`Failed to determine trade action for LongShort="${position.LongShort}", isFutures=${isFutures}`);
    }
    const fullQty = Math.abs(parseFloat(position.Quantity));
    let quantity = fullQty;
    if (quantityOverride != null && quantityOverride !== '') {
        const q = Math.abs(parseFloat(quantityOverride));
        if (!Number.isFinite(q) || q <= 0) {
            throw new Error(`Invalid close quantity: ${quantityOverride}`);
        }
        quantity = Math.min(fullQty, Math.max(1, Math.floor(q)));
    }
    
    // For futures: always use Market orders with DAY duration (no extended hours support)
    // For stocks: use market hours logic (Market/DAY during regular hours, Limit/DYP after hours)
    let orderType, timeInForce, limitPrice;
    
    if (isFutures) {
        orderType = 'Market';
        timeInForce = 'DAY';
        limitPrice = null;
    } else {
        orderType = getClosePositionOrderType();
        timeInForce = getClosePositionTimeInForce();
        limitPrice = getClosePositionLimitPrice(position);
    }
    
    const order = {
        "Symbol": position.Symbol,
        "Quantity": quantity.toString(),
        "OrderType": orderType,
        "TradeAction": tradeAction,
        "TimeInForce": {"Duration": timeInForce}
    }
    
    // Add limit price if it's a limit order (after hours for stocks only)
    if (orderType === 'Limit' && limitPrice) {
        // Format the limit price properly using tick size and precision
        const roundedPrice = roundToTickSizeFromDetails(limitPrice, position.Symbol, tickerDetails);
        const precision = getPrecision(position.Symbol, tickerDetails);
        order["LimitPrice"] = roundedString(roundedPrice, precision);
    }

    return { ...baseOrderPayload(selectedAccount), ...order };
}

// CREATE STOP LOSS ORDER PAYLOAD
const generateStopLossOrderPayload = (selectedAccount, position, stopLoss, breakEven=true, isFutures=false) => {
    const averagePrice = parseFloat(position.AveragePrice)
    const oneRDistance = Math.abs(averagePrice - stopLoss);
    const quantity = Math.abs(position.Quantity);
    const oneRStopPrice = position.LongShort === 'Long' ? averagePrice - oneRDistance : averagePrice + oneRDistance;
    const tradeAction = getTradeAction(position.LongShort, false, isFutures, isOptionSymbol(position.Symbol));

    const order = {
        "Symbol": position.Symbol,
        "Quantity": quantity.toString(),
        "OrderType": 'StopMarket',
        "StopPrice": breakEven ? roundedString(averagePrice) : roundedString(oneRStopPrice),
        "TradeAction": tradeAction,
        "TimeInForce": {"Duration": "DAY"}
    }

    return { ...baseOrderPayload(selectedAccount), ...order };
}

// CREATE TAKE PROFIT ORDER PAYLOAD
const generateTakeProfitOrder = (selectedAccount, position, stopLoss, takeProfitRLevel, takeProfitPercentage, isFutures=false) => {
    const quantity = Math.abs(parseFloat(position.Quantity))
    const avgPrice = parseFloat(position.AveragePrice)
    const oneRDistance = Math.abs(avgPrice - stopLoss);
    const takeProfitDistance = takeProfitRLevel * oneRDistance;
    const takeProfitPrice = position.LongShort === 'Long' ? avgPrice + takeProfitDistance : avgPrice - takeProfitDistance
    const tradeAction = getTradeAction(position.LongShort, false, isFutures, isOptionSymbol(position.Symbol));
    const takeProfitQty = Math.round(quantity * takeProfitPercentage);

    return { // TAKE PROFIT
        ...baseOrderPayload(selectedAccount),
        "Symbol": position.Symbol,
        "Quantity": String(takeProfitQty),
        "LimitPrice": roundedString(takeProfitPrice),
        "OrderType": 'Limit',
        "TradeAction": tradeAction,
        "TimeInForce": {"Duration": "GTC"},
    }
}

// DETERMINE TRADE ACTION: Works for opening a new position or adding bracket for existing position
export const getTradeAction = (longShort, newPosition, isFutures, isOption = false) => {
    if (isOption) {
        if (newPosition) {
            return longShort === 'Long' ? 'BUYTOOPEN' : 'SELLTOOPEN';
        }
        return longShort === 'Long' ? 'SELLTOCLOSE' : 'BUYTOCLOSE';
    }
    if (isFutures) {
        if (newPosition) {
            // For new futures positions, BUY for long, SELL for short
            return longShort === 'Long' ? 'BUY' : 'SELL';
        } else {
            // For closing futures positions, SELL for long, BUY for short
            return longShort === 'Long' ? 'SELL' : 'BUY';
        }
    } else {
        if (newPosition) {
            // For new stock positions, BUY for long, SELLSHORT for short
            return longShort === 'Long' ? 'BUY' : 'SELLSHORT';
        } else {
            // For closing stock positions, SELL for long, BUYTOCOVER for short
            return longShort === 'Long' ? 'SELL' : 'BUYTOCOVER';
        }
    }
}

export const getClosePositionTradeAction = (longShort, isFutures = false, isOption = false) => {
    const ls = (longShort || '').toString().trim().toLowerCase();
    if (ls !== 'long' && ls !== 'short') {
        console.error('[getClosePositionTradeAction] Invalid longShort:', longShort);
        return null;
    }
    const isLong = ls === 'long';
    if (isOption) {
        return isLong ? 'SELLTOCLOSE' : 'BUYTOCLOSE';
    }
    if (isFutures) {
        return isLong ? 'SELL' : 'BUY';
    }
    return isLong ? 'SELL' : 'BUYTOCOVER';
}

/**
 * Prefer the position row for futures vs equity exit actions. Chart `isFutures` can be wrong when
 * ticker details are for a different symbol or still loading.
 */
export const resolveIsFuturesForPositionOrder = (position, contextIsFutures = false) => {
    const at = position?.AssetType?.toUpperCase?.();
    if (at === 'FUTURE') return true;
    if (at === 'STOCK' || at === 'EQUITY' || at === 'ETF') return false;
    const st = position?.SecurityType?.toLowerCase?.();
    if (typeof st === 'string' && st.includes('future')) return true;
    return !!contextIsFutures;
}

export const getTickSizeFromDetails = (ticker, tickerDetails) => {
    // Use PriceFormat.Increment from ticker details if available
    if (tickerDetails?.PriceFormat?.Increment) {
        const inc = parseFloat(tickerDetails.PriceFormat.Increment);
        if (Number.isFinite(inc) && inc > 0) return inc;
    }
    
    // Fallback to hardcoded values if no ticker details
    const t = (ticker ?? '').toString();
    if (t.includes('MNQ') || t.includes('MES')) {
        return 0.25;
    } else if (t.includes('YM') || t.includes('MYM')) {
        return 1.0;
    }
    
    // Default to 0.01 for stocks
    return 0.01;
}

export const roundToTickSizeFromDetails = (price, ticker, tickerDetails) => {
    if (!Number.isFinite(price)) return price;
    const tickSize = getTickSizeFromDetails(ticker, tickerDetails);
    const divided = price / tickSize;
    const rounded = Math.round(divided) * tickSize;
    
    // Calculate precision dynamically based on tick size
    const precision = getPrecision(ticker, tickerDetails);
    return parseFloat(rounded.toFixed(precision));
}

export const getPrecision = (ticker, tickerDetails = null) => {
    const tickSize = getTickSizeFromDetails(ticker, tickerDetails);
    
    // Calculate precision based on tick size decimal places
    let precision;
    if (tickSize >= 1) {
        precision = 0; // Whole numbers (like YM futures)
    } else {
        // Count decimal places in tick size
        const tickSizeStr = tickSize.toString();
        if (tickSizeStr.includes('.')) {
            precision = tickSizeStr.split('.')[1].length;
        } else {
            precision = 0;
        }
    }
    
    return Math.max(0, precision); // Ensure non-negative
}